import { useCallback, useEffect, useRef, useState } from 'react';

import { VideoComposition, totalDuration } from './composition';
import { ensureFonts } from './fonts';
import { fontsUsed } from './export';
import { MediaCache } from './media';
import { PlaybackEngine } from './player';
import { invalidateTextLayouts } from './render';

export interface Playback {
    canvasRef: (el: HTMLCanvasElement | null) => void;
    engine: PlaybackEngine | null;
    time: number;
    playing: boolean;
    duration: number;
    play: () => void;
    pause: () => void;
    toggle: () => void;
    seek: (t: number) => void;
}

/**
 * Binds a PlaybackEngine to a canvas and a composition, and mirrors its clock
 * into React state for the transport and the timeline playhead.
 */
export function usePlayback(comp: VideoComposition): Playback {
    const [engine, setEngine] = useState<PlaybackEngine | null>(null);
    const [time, setTime] = useState(0);
    const [playing, setPlaying] = useState(false);
    const compRef = useRef(comp);
    compRef.current = comp;

    const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
    const canvasRef = useCallback((el: HTMLCanvasElement | null) => setCanvasEl(el), []);

    useEffect(() => {
        if (!canvasEl) return undefined;
        const media = new MediaCache({ posterFallback: true, withSound: true });
        const e = new PlaybackEngine(canvasEl, media);
        e.onTime = (t) => setTime(t);
        e.onPlayingChange = (p) => setPlaying(p);
        e.setComposition(compRef.current);
        setEngine(e);
        return () => {
            e.pause();
            e.dispose();
            media.dispose();
            setEngine(null);
        };
    }, [canvasEl]);

    useEffect(() => {
        if (!engine) return;
        engine.setComposition(comp);
        if (engine.time > totalDuration(comp)) engine.seek(totalDuration(comp));
    }, [engine, comp]);

    // Fonts: re-render once the faces a composition uses have arrived.
    const fontKey = fontsUsed(comp).map((f) => `${f.family}|${f.weight}|${f.style || ''}`).sort().join(',');
    useEffect(() => {
        let cancelled = false;
        void ensureFonts(fontsUsed(compRef.current)).then(() => {
            if (cancelled) return;
            invalidateTextLayouts();
            engine?.requestRender();
        });
        return () => { cancelled = true; };
    }, [fontKey, engine]);

    useEffect(() => () => {
        engine?.pause();
    }, [engine]);

    return {
        canvasRef,
        engine,
        time,
        playing,
        duration: totalDuration(comp),
        play: useCallback(() => engine?.play(), [engine]),
        pause: useCallback(() => engine?.pause(), [engine]),
        toggle: useCallback(() => engine?.toggle(), [engine]),
        seek: useCallback((t: number) => engine?.seek(t), [engine]),
    };
}
